import { ApiCommand } from '~/lib/Api';
import urlConstants from '~/lib/constants/url.constants';
import { removeUUIDFromFilename } from '~/lib/util/chat/chat.utils';

import { useApi } from '..';
import { useLazyGetPatientDocumentDownloadURLQuery } from '~/store/services/chatDocuments';

const { getAttachmentVoidUrl } = urlConstants.clinical_data;

interface GetDocumentDownloadURLArgs {
  fileName: string;
  patientExternalId: string;
  onSuccess?: (documentUrl: string) => void;
  onFailure?: () => void;
}

interface ChatAttachmentState {
  isLoading: boolean;
  removeAttachment: (
    filename: string,
    onSuccess?: (serverMessage: string) => void,
    onFailure?: (serverMessage: string) => void,
  ) => void;

  getDocumentDownloadURL: ({
    fileName,
    patientExternalId,
    onSuccess,
    onFailure,
  }: GetDocumentDownloadURLArgs) => void;
  isFileValid: boolean;
  s3Url: string;
  fileValidationStatus: string;
}

const useChatAttachment = (): ChatAttachmentState => {
  const [getDownloadUrl, { isLoading: attachmentDownloadUrlLoading, data }] =
    useLazyGetPatientDocumentDownloadURLQuery();

  // TODO move this to RTK query
  const {
    sendRequest: getAttachmentVoidSendRequest,
    loading: attachmentVoidLoading,
  } = useApi<{
    returnValue: string;
  }>();

  const removeAttachment = (
    filename: string,
    onSuccess?: (serverMessage: string) => void,
    onFailure?: (serverMessage: string) => void,
  ) => {
    const documentUID = filename?.replace(
      `-${removeUUIDFromFilename(filename)}`,
      '',
    );
    const attachmentVoidUrl = getAttachmentVoidUrl(documentUID);

    getAttachmentVoidSendRequest({
      callback: (response, statusCode) => {
        if (statusCode === 200) {
          onSuccess?.(response.returnValue);
        } else {
          onFailure?.(response?.returnValue || 'Unknown error occurred.');
        }
      },

      command: ApiCommand.PUT,
      options: { isVoid: 'true' },
      url: attachmentVoidUrl,
    });
  };

  const getDocumentDownloadURL = ({
    fileName,
    patientExternalId,
    onSuccess,
    onFailure,
  }: GetDocumentDownloadURLArgs) => {
    getDownloadUrl({
      fileName,
      patientExternalId,
    })
      .unwrap()
      .then((response) => {
        onSuccess?.(response.attachmentDownloadUrl);
      })
      .catch(() => {
        onFailure?.();
      });
  };

  return {
    fileValidationStatus: data?.fileValidationStatus,
    getDocumentDownloadURL,
    isFileValid: data?.isValidFile,
    isLoading: attachmentVoidLoading || attachmentDownloadUrlLoading,
    removeAttachment,
    s3Url: data?.attachmentDownloadUrl,
  };
};

export default useChatAttachment;
